import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { format, isValid, parse } from 'date-fns';
import { BulkApproveDateGroup, BulkApproveItemState, WorkOrderItem } from '../../../models/work-orders-overview.models';
import { WorkOrderPriority, WorkOrderPriorityLabels, WorkOrderServiceTypeLabels } from '../../../enums/work-order.enum';
import { ServiceTypes } from '../../../../../_shared/enums/service-type.enum';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TechnicianService } from '../../../services/technician.service';
import { FlatpickrDatePickerComponent } from '../../../../../../_shared/components/flatpickr-date-picker/flatpickr-date-picker.component';
import { WorkOrderApproval } from '../../../models/work-order-requests-overview.models';
import { ErrorHandlerService } from '../../../../../../_shared/services/error-handler.service';
import { WorkOrderTechnician } from '../../../models/technician.model';
import { TechnicianMultiSelectDropdownComponent } from '../../_shared/technician-multi-select-dropdown/technician-multi-select-dropdown.component';

@Component({
  selector: 'app-bulk-approve-list-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, NgbTooltipModule, FlatpickrDatePickerComponent, TechnicianMultiSelectDropdownComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './bulk-approve-list-modal.component.html',
  styleUrl: './bulk-approve-list-modal.component.scss'
})
export class BulkApproveListModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private technicianService = inject(TechnicianService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  @Output() itemRemoved = new EventEmitter<number>();
  @Input() onSubmit: (items: WorkOrderApproval[]) => Promise<boolean> = async () => false;

  workOrders: WorkOrderItem[] = [];
  currencySymbol: string = 'R';

  items: BulkApproveItemState[] = [];
  technicians: WorkOrderTechnician[] = [];
  isLoadingTechnicians: boolean = false;
  isSubmitting: boolean = false;

  WorkOrderPriorityLabels = WorkOrderPriorityLabels;
  WorkOrderServiceTypeLabels = WorkOrderServiceTypeLabels;
  ServiceTypes = ServiceTypes;

  private readonly cancelSubject = new Subject<void>();

  ngOnInit(): void {
    this.items = this.workOrders.map(wo => ({
      workOrder: wo,
      scheduledDate: this.formatBackendDate(wo.scheduledDate),
      technicianIds: []
    }));

    this.loadTechniciansAsync(this.workOrders.map(x => x.workOrderId));
  }

  get dateGroups(): BulkApproveDateGroup[] {
    const groups = new Map<string, BulkApproveItemState[]>();
    for (const item of this.items) {
      const label = this.formatGroupDate(item.workOrder.dateCreated);
      if (!groups.has(label)) {
        groups.set(label, []);
      }
      groups.get(label)!.push(item);
    }
    return Array.from(groups.entries()).map(([dateLabel, items]) => ({ dateLabel, items }));
  }

  get isValid(): boolean {
    return this.items.length > 0 && this.items.every(i => i.scheduledDate && i.technicianIds.length > 0);
  }

  getPriorityClass(priority: WorkOrderPriority): string {
    switch (priority) {
      case WorkOrderPriority.Low: return 'priority-low';
      case WorkOrderPriority.Medium: return 'priority-medium';
      case WorkOrderPriority.High: return 'priority-high';
      default: return '';
    }
  }

  getPriorityLabel(priority: WorkOrderPriority): string {
    return this.transloco.translate('workOrder.modals.bulkApprove.priorityLabel', {
      priority: this.transloco.translate(WorkOrderPriorityLabels[priority])
    });
  }

  formatCost(cost: number): string {
    if (!cost) return '-';
    return `${this.currencySymbol} ${cost.toLocaleString()}`;
  }

  formatTime(time: number): string {
    if (!time) return '-';
    return `${time} ${this.transloco.translate('workOrder.table.hrs')}`;
  }

  onScheduledDateChange(item: BulkApproveItemState, value: string | undefined): void {
    item.scheduledDate = value ?? '';
  }

  removeItem(item: BulkApproveItemState): void {
    this.itemRemoved.emit(item.workOrder.workOrderId);
    this.items = this.items.filter(i => i !== item);
    if (this.items.length === 0) {
      this.activeModal.dismiss();
    }
  }

  onCancel(): void {
    this.activeModal.dismiss();
  }

  async onApproveAll(): Promise<void> {
    if (!this.isValid || this.isSubmitting) return;

    const result: WorkOrderApproval[] = this.items.map(item => ({
      workOrderId: item.workOrder.workOrderId,
      scheduledDate: this.parseScheduledDate(item.scheduledDate),
      technicianIds: item.technicianIds
    }));

    this.isSubmitting = true;
    await this.onSubmit(result);
    this.isSubmitting = false;
  }

  getWorkOrderTechnicians(workOrderId: number): WorkOrderTechnician[] {
    return this.technicians.filter(x => x.workOrderId === workOrderId) ?? [];
  }

  private async loadTechniciansAsync(workOrderIds: number[]): Promise<void> {
    try {
      this.isLoadingTechnicians = true;
      const response = await this.technicianService.getTechniciansAsync(workOrderIds, this.cancelSubject);
      this.technicians = response.data ?? [];
      this.isLoadingTechnicians = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => this.isLoadingTechnicians = false
      });
    }
  }

  private formatBackendDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return format(d, 'yyyy-MM-dd HH:mm');
  }

  private formatGroupDate(date: Date | string): string {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private parseScheduledDate(dateStr: string): Date {
    const parsedDateTime = parse(dateStr.trim(), 'yyyy-MM-dd HH:mm', new Date());
    if (isValid(parsedDateTime)) return parsedDateTime;

    const parsedDateOnly = parse(dateStr.trim(), 'yyyy-MM-dd', new Date());
    return isValid(parsedDateOnly) ? parsedDateOnly : new Date('');
  }
}